import { useCallback, useEffect, useState } from "react";
import { fetchInvoiceFile, saveBlob } from "@/lib/invoiceFile";

const idleState = (isLoading) => ({ blob: null, isLoading, error: null });

/**
 * Fetches an invoice PDF once and owns the resulting blob.
 *
 * The old app fetched the file inside `PDFViewer` and pushed the blob back out
 * through a `setFileData` prop so the parent's download button could reach it.
 * The fetch lives here instead, and both the viewer and the download button
 * read from the same hook.
 */
export function useInvoiceFile(invoiceGuid) {
  const [state, setState] = useState(() => idleState(Boolean(invoiceGuid)));
  const [loadedGuid, setLoadedGuid] = useState(invoiceGuid);

  // Reset while rendering the new invoice rather than in the effect, so the
  // previous invoice's document is never shown against the new one.
  if (loadedGuid !== invoiceGuid) {
    setLoadedGuid(invoiceGuid);
    setState(idleState(Boolean(invoiceGuid)));
  }

  useEffect(() => {
    if (!invoiceGuid) return undefined;

    let cancelled = false;

    fetchInvoiceFile(invoiceGuid)
      .then((blob) => {
        if (cancelled) return;
        setState({ blob, isLoading: false, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ blob: null, isLoading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [invoiceGuid]);

  const download = useCallback(
    (fileName = "invoice.pdf") => {
      if (!state.blob) return;
      saveBlob(state.blob, fileName);
    },
    [state.blob],
  );

  return { ...state, download };
}
